/**
 * Todos sagas — async logic, API calls, WebSocket event handling.
 */

import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as api from '@/infra/api/client';
import { SYNC_REFRESH } from '@/infra/sync/actions';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  setTodos,
  addTodo,
  updateTodo,
  replaceTodo,
  removeTodo,
  setLoading,
  setError,
  setSmartLoading,
  setSmartError,
  setLastSmartTodo,
  setLastSmartResult,
  FETCH_TODOS,
  CREATE_TODO,
  SMART_CREATE_TODO,
  UPDATE_TODO_REQUESTED,
  DELETE_TODO,
  SCHEDULE_COMPLETE_REQUESTED,
  CANCEL_SCHEDULED_COMPLETE_REQUESTED,
  CLAIM_TODO_REQUESTED,
  SKIP_TODO_REQUESTED,
  MOVE_TODO_REQUESTED,
  MARK_BLOCKED_REQUESTED,
  UNBLOCK_TODO_REQUESTED,
} from './ducks';
import { addNotification } from '@/features/notifications/ducks';
import { completionLogRowChanged } from './completion-log-ducks';
import { Notification, Todo } from '@/infra/types/domain';
import { UpdateTodoRequest, SmartTodoResult } from '@/infra/types/api';
import { runClientValidations } from '@/features/predicate/validation';
import { AppDefaults } from '@/infra/wasm/AppDefaults';
import { TOAST_AUTO_DISMISS_MS } from '@/infra/ui/toastConfig';
import {
  buildTodoContext,
  defaultHostFrame,
  evalDisplayThrowing,
} from '@/features/predicate/walker';
import { latestRules } from '@/features/predicate/rule-cache';
import { renderSegments } from '@/features/predicate/renderSegments';
import { selectUserId } from '@/features/auth/selectors';

// ─── Fetch Todos ───────────────────────────────────────────

export function* fetchTodosSaga(action?: { type: string; payload?: Record<string, string> }): Generator {
  yield call(withSagaFlow, 'todos.fetch', ['todos'], action, function* () {
    yield put(setLoading(true));
    try {
      const todos = (yield call(api.getTodos, action?.payload)) as Todo[];
      yield* emitSagaPoint('api_getTodos_ok', { count: todos.length });
      yield put(setTodos(todos));
      yield put(setError(null));
    } catch (error: any) {
      yield put(setError(error.message || 'Failed to fetch todos'));
      yield* emitSagaPoint('api_getTodos_err', { message: error?.message });
    } finally {
      yield put(setLoading(false));
    }
  });
}

// ─── Create Todo ───────────────────────────────────────────

export function* createTodoSaga(action: {
  type: string;
  payload: {
    title: string;
    owner: string;
    type_tag: string;
    vanishing_enabled?: boolean;
    vanish_at_time?: string;
    [key: string]: unknown;
  };
}): Generator {
  yield call(withSagaFlow, 'todos.create', ['todos'], action, function* () {
    try {
      const { title, owner, type_tag } = action.payload;
      const vanishingEnabled = action.payload.vanishing_enabled ?? false;
      const vanishAtTime = vanishingEnabled ? (action.payload.vanish_at_time ?? AppDefaults.vanishDefaultTime) : undefined;

      const apiPayload: Record<string, unknown> = {
        title,
        owner_id: owner,
        type_tag,
        source_plugin: 'manual',
        vanishing_enabled: vanishingEnabled,
      };

      if (vanishAtTime !== undefined) {
        apiPayload.vanish_at_time = vanishAtTime;
      }

      // Pre-flight against the loaded validation.* rules so the
      // user sees the rule's messageTemplate inline instead of
      // waiting for the server's 400. Walker errors / no rules
      // loaded → noop; server's authoritative gate still runs.
      const candidate: Todo = {
        id: '',
        title,
        owner_id: owner,
        type_tag,
        source_plugin: 'manual',
        status: 'pending',
        priority: 'normal',
        due_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        recurrence_rule: '',
        vanishing_enabled: vanishingEnabled,
        vanish_at_time: vanishAtTime ?? '',
        notify_enabled: false,
        notify_before: 0,
        visibility: 'public',
      } as Todo;
      const failure = runClientValidations(candidate, defaultHostFrame('', '', []));
      if (failure) {
        yield put(setError(failure.message));
        yield* emitSagaPoint('api_createTodo_validation_failed', {
          rule_id: failure.ruleID,
          message: failure.message,
        });
        return;
      }

      const todo = (yield call(api.createTodo, apiPayload as any)) as Todo;
      yield* emitSagaPoint('api_createTodo_ok', { todo_id: todo.id });
      yield put(addTodo(todo));
      yield put(setError(null));
    } catch (error: any) {
      const message = error.message || '';
      yield put(setError(message ? `Failed to create todo: ${message}` : 'Failed to create todo'));
      yield* emitSagaPoint('api_createTodo_err', { message });
    }
  });
}

// ─── Smart Create Todo (Claude-powered) ───────────────────

export function* smartCreateTodoSaga(action: {
  type: string;
  payload: { text: string };
}): Generator {
  // Threads through the HTTP request via the X-QPL-ID interceptor so the
  // server attaches its middleware points to the same flow id this saga
  // participates in (under whichever nav flow is active).
  yield call(withSagaFlow, 'smart_create_todo', ['todos'], action, function* () {
    yield put(setSmartLoading(true));
    yield put(setSmartError(null));
    yield put(setLastSmartTodo(null));
    yield put(setLastSmartResult(null));
    try {
      const result = (yield call(api.createSmartTodo, action.payload.text)) as SmartTodoResult;
      yield* emitSagaPoint('api_createSmartTodo_ok', { action: result.action });
      yield put(setLastSmartResult(result));

      switch (result.action) {
        case 'deleted': {
          if (result.deleted_todo_id) {
            yield put(removeTodo(result.deleted_todo_id));
          }
          break;
        }
        case 'completed': {
          // Server response carries the full post-write Todo under
          // `todo`. Replay it via replaceTodo so proto3 `optional`
          // fields that the server cleared (close_reason, due_at on
          // a once-recurring row, etc.) don't get shadowed by stale
          // state. Fall back to a partial merge only if `todo` is
          // somehow absent — a defense against pre-v0.2 servers; can
          // be removed once that's irrelevant.
          const completed = result.todo as Todo | undefined;
          if (completed) {
            yield put(replaceTodo(completed));
          } else if (result.completed_todo_id) {
            yield put(updateTodo(result.completed_todo_id, { status: 'done', close_reason: 'completed' }));
          }
          break;
        }
        case 'updated': {
          // Per feedback_saga_full_delta: server returns the full
          // post-update Todo on `todo`, so the saga replays the delta
          // via replaceTodo. No FETCH_TODOS hammer, no WS dependency.
          const updated = result.todo as Todo | undefined;
          if (updated) {
            yield put(replaceTodo(updated));
          }
          break;
        }
        case 'created':
        default: {
          // Server returns {action, todo, todos, updated_fields}. For
          // multi-instance series ("create 3 daily series ..."), `todos`
          // carries every row; the saga fans out addTodo per element so
          // the inbox shows all N rows immediately. For ordinary single-
          // row creates `todos` is length 1 (or absent on older servers,
          // in which case fall back to the legacy single `todo` field).
          // The proto-generated SmartTodoResponse.todos type is the wire
          // Todo (string-valued notify_before etc.); the saga store
          // holds the domain Todo. The existing single-`todo` path
          // casts the same way — see line 175.
          const todosArr = (result.todos ?? []) as unknown as Todo[];
          if (todosArr.length > 0) {
            for (const t of todosArr) {
              yield put(addTodo(t));
            }
            yield put(setLastSmartTodo(todosArr[0]));
          } else {
            const todo = result.todo as Todo | undefined;
            if (todo) {
              yield put(addTodo(todo));
              yield put(setLastSmartTodo(todo));
            }
          }
          break;
        }
      }

      yield put(setSmartError(null));
    } catch (error: any) {
      const message = error.message || '';
      yield put(setSmartError(message ? `Failed to process smart todo: ${message}` : 'Failed to process smart todo'));
      yield* emitSagaPoint('api_createSmartTodo_err', { message });
    } finally {
      yield put(setSmartLoading(false));
    }
  });
}

// ─── Update Todo ───────────────────────────────────────────

export function* updateTodoSaga(action: {
  type: string;
  payload: { id: string; changes: Partial<Todo> & { _scope?: 'instance' | 'series' } };
}): Generator {
  yield call(withSagaFlow, 'todos.update', ['todos'], action, function* () {
    try {
      // Lift `_scope` off the changes payload — it's metadata for the API
      // call, not a field on the todo. TodoDetailView attaches it when the
      // user picks "Just this one" / "Entire series" on a recurring edit.
      const { _scope, ...rest } = action.payload.changes;
      const scope = _scope as 'instance' | 'series' | undefined;
      const updated = (yield call(
        api.updateTodo,
        action.payload.id,
        rest as UpdateTodoRequest,
        scope,
      )) as Todo;
      yield* emitSagaPoint('api_updateTodo_ok');
      // Reopen (status done → pending) clears completed_at + close_reason
      // server-side, but the JSON encoder may still serialize empty
      // strings or — for nested non-optional `optional Timestamp` —
      // include the prior value zeroed-out. Explicitly stamp the
      // post-transition fields so the row leaves Completed Today on the
      // very next render instead of waiting for a refetch.
      const normalized = (rest as Partial<Todo>).status === 'pending'
        ? { ...updated, completed_at: undefined, close_reason: '' }
        : updated;
      // Replace, not merge: server omits cleared proto3 `optional` fields
      // (e.g. due_at cleared via drag-to-Inbox). Merging leaves the old
      // due_at in state and the todo stays under Today until a refetch.
      yield put(replaceTodo(normalized as Todo));
      yield put(setError(null));
    } catch (error: any) {
      const message = error.message || '';
      yield put(setError(message ? `Failed to update todo: ${message}` : 'Failed to update todo'));
      yield* emitSagaPoint('api_updateTodo_err', { message });
    }
  });
}

// ─── Delete Todo ───────────────────────────────────────────

export function* deleteTodoSaga(action: {
  type: string;
  payload: string;
}): Generator {
  if (!action.payload) return;

  yield call(withSagaFlow, 'todos.delete', ['todos'], action, function* () {
    try {
      yield call(api.deleteTodo, action.payload);
      yield* emitSagaPoint('api_deleteTodo_ok');
      yield put(removeTodo(action.payload));
      yield put(setError(null));
    } catch (error: any) {
      // 404 means todo is already gone on the backend — remove from UI
      if (error.status === 404) {
        yield put(removeTodo(action.payload));
        yield put(setError(null));
        yield* emitSagaPoint('api_deleteTodo_err', { status: 404, treated_as: 'already_gone' });
        return;
      }
      // All other errors: keep todo in state and report error
      const message = error.message || 'Failed to delete todo';
      yield put(setError(message));
      yield* emitSagaPoint('api_deleteTodo_err', { message });
    }
  });
}

// ─── Skip (advance recurring todo to next occurrence) ──────

export function* skipTodoSaga(action: {
  type: string;
  payload: { id: string };
}): Generator {
  yield call(withSagaFlow, 'todos.skip', ['todos'], action, function* () {
    try {
      const updated = (yield call(api.skipTodo, action.payload.id)) as Todo;
      yield* emitSagaPoint('api_skipTodo_ok');
      yield put(replaceTodo(updated));
      yield put(setError(null));
    } catch (error: any) {
      const message = error.message || '';
      yield put(setError(message ? `Failed to skip todo: ${message}` : 'Failed to skip todo'));
      yield* emitSagaPoint('api_skipTodo_err', { message });
    }
  });
}

// ─── Schedule / Cancel Completion (Temporal-backed) ────────

// Kicks off a durable 6-second-delayed completion workflow on the server.
// Redux state is NOT optimistically mutated here — TodoItem owns the local
// pending/optimistic UI state. When the workflow fires, the todo-ws plugin
// broadcasts todo.updated and WS_TODO_UPDATED reconciles Redux.
export function* scheduleCompleteSaga(action: {
  type: string;
  payload: { id: string };
}): Generator {
  yield call(withSagaFlow, 'todos.schedule_complete', ['todos'], action, function* () {
    try {
      yield call(api.scheduleTodoComplete, action.payload.id);
      yield* emitSagaPoint('api_scheduleTodoComplete_ok');
    } catch (error: any) {
      const message = error.message || 'Failed to schedule completion';
      yield put(setError(message));
      yield* emitSagaPoint('api_scheduleTodoComplete_err', { message });
    }
  });
}

// Best-effort cancel. If the workflow already fired, the server returns 200
// (idempotent) and WS will land with status='done' anyway — the TodoItem
// optimistic state resets on todo.status change.
export function* cancelScheduledCompleteSaga(action: {
  type: string;
  payload: { id: string };
}): Generator {
  yield call(withSagaFlow, 'todos.cancel_scheduled_complete', ['todos'], action, function* () {
    try {
      yield call(api.cancelTodoComplete, action.payload.id);
      yield* emitSagaPoint('api_cancelTodoComplete_ok');
    } catch (error: any) {
      // Non-blocking — swallow. UI will reconcile via WS if workflow already fired.
      yield* emitSagaPoint('api_cancelTodoComplete_err', { message: error?.message });
    }
  });
}

// ─── Claim / Assign ────────────────────────────────────────

// Optimistically flip owner_id so the row moves columns immediately.
// Server response (authoritative) replaces the row on success; the WS
// broadcast from todo-ws also lands on both owners and idempotently
// re-applies the same state. On failure, revert to prior owner.
//
// For instance-scope claim on a recurring todo, we skip the optimistic
// flip — the series row keeps its old owner; the server's response is the
// new clone, and we addTodo it instead.
export function* claimTodoSaga(action: {
  type: string;
  payload: { id: string; newOwnerId: string; scope?: 'instance' | 'series' };
}): Generator {
  yield call(withSagaFlow, 'todos.claim', ['todos'], action, function* () {
    const { id, newOwnerId, scope } = action.payload;
    try {
      if (scope !== 'instance') {
        // Optimistic update — flip owner locally for series scope (and the
        // legacy unscoped path, which is series-equivalent on the server).
        yield put(updateTodo(id, { owner_id: newOwnerId } as Partial<Todo>));
      }
      const updated = (yield call(api.claimTodo, id, newOwnerId, scope)) as Todo;
      yield* emitSagaPoint('api_claimTodo_ok');
      if (scope === 'instance') {
        // Server returns the freshly forked clone. Series row stays and
        // arrives via WS as an update on its own frame.
        yield put(addTodo(updated));
      } else {
        yield put(replaceTodo(updated));
      }
      yield put(setError(null));
    } catch (error: any) {
      // Revert: refetch to restore the authoritative pre-claim state.
      yield put({ type: FETCH_TODOS });
      const message = error.message || 'Failed to claim todo';
      yield put(setError(message));
      yield* emitSagaPoint('api_claimTodo_err', { message });
    }
  });
}

// ─── Move (bucket transition) ──────────────────────────────

// Server owns the bucket policy: today → end-of-day in caller TZ +
// due_date_only, inbox → cleared. Both web drag and iOS swipe call this
// same RPC so the two clients can't drift again. No optimistic update —
// the response carries the authoritative due_at and `replaceTodo`
// overwrites in place (proto3 omits cleared optionals on the wire, so a
// merge would shadow the server's clear with stale state).
export function* moveTodoSaga(action: {
  type: string;
  payload: { id: string; bucket: 'today' | 'inbox' | 'tomorrow' };
}): Generator {
  yield call(withSagaFlow, 'todos.move', ['todos'], action, function* () {
    try {
      const updated = (yield call(api.moveTodo, action.payload.id, action.payload.bucket)) as Todo;
      yield* emitSagaPoint('api_moveTodo_ok', { bucket: action.payload.bucket });
      yield put(replaceTodo(updated));
      yield put(setError(null));
      if (action.payload.bucket === 'tomorrow') {
        yield put(
          addNotification({
            id: `moved-tomorrow-${updated.id}-${Date.now()}`,
            type: 'todo_blocked_followup',
            title: 'Moved to tomorrow',
            message: updated.title,
            status: 'unread',
            action_url: `/todos?todo=${updated.id}`,
            created_at: new Date().toISOString(),
            auto_dismiss_ms: TOAST_AUTO_DISMISS_MS,
          }),
        );
      }
    } catch (error: any) {
      const message = error.message || '';
      yield put(setError(message ? `Failed to move todo: ${message}` : 'Failed to move todo'));
      yield* emitSagaPoint('api_moveTodo_err', { message });
    }
  });
}

// ─── Mark Blocked ──────────────────────────────────────────

export function* markBlockedTodoSaga(action: {
  type: string;
  payload: { id: string };
}): Generator {
  yield call(withSagaFlow, 'todos.mark_blocked', ['todos'], action, function* () {
    try {
      const result = (yield call(api.markBlocked, action.payload.id)) as api.MarkBlockedResult;
      yield* emitSagaPoint('api_markBlocked_ok', { followup_id: result.followup?.id });

      // Saga is the orchestration boundary: dispatch every state change
      // the server made, sourced from the response. No WS-dependency,
      // no refetch — the local store mirrors the post-write state the
      // instant the API returns.
      //   - original → status=done + close_reason=blocked
      //   - followup → fresh row in active list
      //   - completion → appended to log
      if (result.original) {
        yield put(replaceTodo(result.original));
      }
      if (result.followup) {
        yield put(addTodo(result.followup));
      }
      if (result.completion) {
        yield put(completionLogRowChanged({ type: 'added', completion: result.completion }));
      }

      // Toast body comes entirely from rule.display.toast.blocked_followup
      // — the rule is the single source of truth, identical across
      // web/iOS/Go. The saga's only job is to walk it with the
      // follow-up's context. Cold-start (rule not synced) falls back
      // to a no-body toast; the rule will be there by the next dispatch.
      const rules = latestRules();
      const rule = rules?.get('rule.display.toast.blocked_followup');
      const userID = (yield select(selectUserId)) as string | null;
      let bodyRuns: Notification['body_runs'] | undefined;
      let bodyText = '';
      if (rule?.display?.body && result.followup) {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
          const host = defaultHostFrame(userID ?? '', tz, []);
          const ctx = buildTodoContext(result.followup, host);
          const fs = evalDisplayThrowing(rule.display.body, ctx);
          const runs = renderSegments(fs, {
            locale: navigator.language || 'en-US',
            timeZone: tz,
          });
          if (runs.length > 0) {
            bodyRuns = runs;
            bodyText = runs.map((r) => r.text).join('');
          }
        } catch {
          // Walker trap — leave bodyText empty; QPL fallback flow
          // captures the trap.
        }
      }

      const notification: Notification = {
        id: `blocked-followup-${result.followup?.id ?? action.payload.id}-${Date.now()}`,
        type: 'todo_blocked_followup',
        title: 'Marked as blocked',
        message: bodyText,
        body_runs: bodyRuns,
        status: 'unread',
        // The /todos route is the page; the detail modal opens via the
        // ?todo=<id> query param (TodoItem.openModal). A bare /todos/<id>
        // path 404s.
        action_url: result.followup ? `/todos?todo=${result.followup.id}` : null,
        created_at: new Date().toISOString(),
        auto_dismiss_ms: TOAST_AUTO_DISMISS_MS,
      };
      yield put(addNotification(notification));
      yield put(setError(null));
    } catch (error: any) {
      // 409 + TODO_NOT_BLOCKABLE: the original was already completed
      // / moved out from under us in the eligibility race. No optimistic
      // rollback needed — the saga didn't mutate Redux on the way in.
      if (error?.code === 'TODO_NOT_BLOCKABLE' || error?.status === 409) {
        const msg = "Couldn't block — the todo was already completed or moved.";
        yield put(setError(msg));
        yield put(
          addNotification({
            id: `mark-blocked-conflict-${action.payload.id}-${Date.now()}`,
            type: 'todo_blocked_followup',
            title: 'Mark blocked failed',
            message: msg,
            status: 'unread',
            action_url: null,
            created_at: new Date().toISOString(),
            auto_dismiss_ms: TOAST_AUTO_DISMISS_MS,
          }),
        );
        yield* emitSagaPoint('api_markBlocked_conflict', { code: error?.code });
        return;
      }
      const message = error.message || '';
      yield put(setError(message ? `Failed to mark blocked: ${message}` : 'Failed to mark blocked'));
      yield* emitSagaPoint('api_markBlocked_err', { message });
    }
  });
}

// ─── Unblock (reverse a "blocked" completion) ──────────────

// Fires UnblockTodo on the server, which:
//   1) clears close_reason on the original todo (resurrecting it), and
//   2) tombstones the follow-up todo created at mark-blocked time.
// Both effects propagate through the existing WS fan-out (todo.updated
// for the original, sync delta for the follow-up tombstone). A
// belt-and-braces FETCH_TODOS dispatch is included so the inbox refresh
// doesn't depend on the WS being connected at click time — important
// for the completion-log surface, which is paginated server-side and
// has no WS subscription of its own.
//
// The CompletionLog component optimistically drops the row on click;
// the saga does not push state into that component (it's local, not
// Redux). On error we surface a toast via NotificationCenter and rely
// on the next refetch / WS reconcile to restore the row if the
// optimistic remove was wrong.
//
// Idempotent: the server returns `already_unblocked=true` when there's
// nothing to undo. We treat the two outcomes identically — no special
// UI for the already-unblocked case (a 2-user system isn't going to hit
// it often, and surfacing it would just be noise per
// `feedback_no_consent_ui_local.md`'s "one click reverses" spirit).
export function* unblockTodoSaga(action: {
  type: string;
  payload: { id: string };
}): Generator {
  yield call(withSagaFlow, 'todos.unblock', ['todos'], action, function* () {
    try {
      const result = (yield call(api.unblock, action.payload.id)) as api.UnblockResult;
      yield* emitSagaPoint('api_unblockTodo_ok', {
        already_unblocked: result.already_unblocked,
      });
      // Saga is the orchestration boundary: replay the server's full
      // delta on the local store.
      //   - followup_id → drop from todos byId (bounces Upcoming row immediately)
      //   - original → status back to pending, lands in active list
      //   - completion_id → drop from any cached log view
      // No FETCH_TODOS hammer, no WS dependency.
      if (result.followup_id) {
        yield put(removeTodo(result.followup_id));
      }
      if (result.original) {
        yield put(replaceTodo(result.original));
      }
      if (result.completion_id) {
        yield put(completionLogRowChanged({ type: 'removed', id: result.completion_id }));
      }
      yield put(setError(null));
    } catch (error: any) {
      const message = error.message || '';
      const display = message ? `Failed to undo: ${message}` : 'Failed to undo';
      yield put(setError(display));
      yield put(
        addNotification({
          id: `unblock-error-${action.payload.id}-${Date.now()}`,
          type: 'todo_blocked_followup',
          title: 'Undo failed',
          message: display,
          status: 'unread',
          action_url: null,
          created_at: new Date().toISOString(),
          auto_dismiss_ms: TOAST_AUTO_DISMISS_MS,
        }),
      );
      yield* emitSagaPoint('api_unblockTodo_err', { message });
    }
  });
}

// ─── WebSocket Event Handlers ──────────────────────────────

function* handleWsTodoCreated(action: { type: string; payload: Todo }): Generator {
  yield put(addTodo(action.payload));
}

function* handleWsTodoUpdated(action: {
  type: string;
  payload: { id: string; changes: Partial<Todo>; full_object?: Todo };
}): Generator {
  // full_object is authoritative — replace state outright so cleared
  // proto3 `optional` fields (omitted from the broadcast) don't shadow
  // the previous value. Fall back to partial merge when only changes
  // are available.
  if (action.payload.full_object) {
    yield put(replaceTodo(action.payload.full_object));
  } else {
    yield put(updateTodo(action.payload.id, action.payload.changes));
  }
}

// ─── Root Todos Saga ───────────────────────────────────────

export function* todosSaga(): Generator {
  yield takeLatest(FETCH_TODOS, fetchTodosSaga);
  yield takeEvery(CREATE_TODO, createTodoSaga);
  yield takeEvery(SMART_CREATE_TODO, smartCreateTodoSaga);
  yield takeEvery(UPDATE_TODO_REQUESTED, updateTodoSaga);
  yield takeEvery(DELETE_TODO, deleteTodoSaga);
  yield takeEvery(SCHEDULE_COMPLETE_REQUESTED, scheduleCompleteSaga);
  yield takeEvery(CANCEL_SCHEDULED_COMPLETE_REQUESTED, cancelScheduledCompleteSaga);
  yield takeEvery(CLAIM_TODO_REQUESTED, claimTodoSaga);
  yield takeEvery(SKIP_TODO_REQUESTED, skipTodoSaga);
  yield takeEvery(MOVE_TODO_REQUESTED, moveTodoSaga);
  yield takeEvery(MARK_BLOCKED_REQUESTED, markBlockedTodoSaga);
  yield takeEvery(UNBLOCK_TODO_REQUESTED, unblockTodoSaga);

  // WebSocket event handlers
  yield takeEvery('WS_TODO_CREATED', handleWsTodoCreated);
  yield takeEvery('WS_TODO_UPDATED', handleWsTodoUpdated);

  // Any broadcasts that fired while we were disconnected or suspended
  // are gone — refetch so the store catches up. The sync coordinator
  // decides *when* that is safe (tab visible, server actually
  // reachable); this saga only says what to fetch.
  yield takeLatest(SYNC_REFRESH, fetchTodosSaga);
}
